// material-ui
import {
  Autocomplete,
  Box,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  Input,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@material-ui/core'
// hooks
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import useLazyEffect from 'src/hooks/useLazyEffect'
import { useFiles, useFileDetails } from 'src/api/file/list'
import { updateDetails } from 'src/api/file'
import { getClinicalHistory } from 'src/api/vcf/clinicalHistory'

// constants
import { HPO_OPTIONS } from 'src/constants'

// components
import Tags from 'src/components/Tags'
import DiseaseFilterWrapper from 'src/components/DiseaseFilterWrapper'

// api utils
import { updateTrio, useHpo } from '../../api/vcf'
import diseaseService from 'src/api/filters/diseaseService'

// Sample details (phenotype, diseases, clinical history, notes, age, sex) for one
// sample. This was the "Sample Details" tab of the sample dashboard; it now opens
// from the Home table in a dialog, which triggers the save through the ref
// (`ref.current.save()` resolves true on success, false on failure).

const TrioSelector = function ({ options, trio = {}, sampleName, fileId }) {
  const [motherOption, setMotherOption] = useState({
    label: trio?.mother_sample_name || '',
    value: { fileId: trio?.mother_file || undefined, sample: trio?.mother_sample_name || undefined },
  })
  const [fatherOption, setFatherOption] = useState({
    label: trio?.father_sample_name || '',
    value: { fileId: trio?.father_file || undefined, sample: trio?.father_sample_name || undefined },
  })

  useLazyEffect(() => {
    updateTrio(fileId, { mother: motherOption.value, father: fatherOption.value })
  }, [fileId, motherOption, fatherOption])

  return (
    <Grid container direction="row" spacing={1}>
      <Grid item xs={6}>
        <Autocomplete
          options={options?.filter(
            (o) =>
              ![trio?.mother_sample_name, trio?.father_sample_name, sampleName, fatherOption.value.sample].includes(
                o.value.sample,
              ),
          )}
          renderInput={(params) => <TextField {...params} label="Mother" variant="outlined" />}
          value={motherOption.value.sample ? motherOption : null}
          getOptionLabel={(option) => option.label || ''}
          isOptionEqualToValue={(option, value) => option.value.sample === value.value.sample}
          onChange={(_e, newMother) => {
            newMother = newMother || {
              label: '',
              value: { fileId: undefined, sample: undefined },
            }

            setMotherOption(newMother)
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <Autocomplete
          options={options.filter(
            (o) =>
              ![trio?.mother_sample_name, trio?.father_sample_name, sampleName, motherOption.value.sample].includes(
                o.value.sample,
              ),
          )}
          renderInput={(params) => <TextField {...params} label="Father" variant="outlined" />}
          value={fatherOption.value.sample ? fatherOption : null}
          getOptionLabel={(option) => option.label || ''}
          isOptionEqualToValue={(option, value) => option.value.sample === value.value.sample}
          onChange={(_e, newFather) => {
            newFather = newFather || {
              label: '',
              value: { fileId: undefined, sample: undefined },
            }

            setFatherOption(newFather)
          }}
        />
      </Grid>
    </Grid>
  )
}

const createTrioOptions = (rows) => {
  if (!Array.isArray(rows)) {
    return []
  }
  return rows.map((row) => ({
    label: row.sample,
    value: {
      fileId:
        row.file_type === 'fastq'
          ? row.fastq_pair_id
          : row.file_type === 'ont_bam'
          ? row.ont_bam_id
          : row.file_type === 'pacbio_bam'
          ? row.pacbio_bam_id
          : row.vcf_id,
      sample: row.sample,
    },
  }))
}

// We might as well just remove this component
// deleteFromDB is basically react-query hpoList setter
const ManageHpo = function ({ hpoList, setHpoList, deleteFromDB, association }) {
  const associationObj = {
    name: association,
    type: 'Sample',
  }
  return (
    <Tags
      title="Symptoms"
      options={HPO_OPTIONS}
      value={hpoList}
      onChange={setHpoList}
      deleteFromDB={deleteFromDB}
      association={associationObj}
    />
  )
}

const countChipSx = {
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
  fontWeight: 600,
  height: '24px',
  '& .MuiChip-label': {
    px: 1.5,
    fontSize: '0.75rem',
  },
}

const SampleDetailsPanel = forwardRef(function SampleDetailsPanel({ fileId, sampleName, onCanSaveChange }, ref) {
  const [hpoList, setHpoList] = useHpo({ fileId })
  const [localHpoList, setLocalHpoList] = useState([])
  const [localDiseases, setLocalDiseases] = useState([])
  const [clinicalHistory, setClinicalHistory] = useState('')

  useEffect(() => {
    if (hpoList.length > 0) {
      setLocalHpoList(hpoList)
    }
  }, [hpoList])

  useEffect(() => {
    const fetchDiseases = async () => {
      if (!fileId) return
      try {
        const diseases = await diseaseService.getDiseases(fileId)
        // if it returned something and not 404, set the diseases
        if (diseases && diseases.length > 0) {
          setLocalDiseases(diseases)
        }
      } catch (error) {
        console.error('Error fetching diseases:', error)
      }
    }

    const fetchClinicalHistory = async () => {
      if (!fileId) return
      try {
        const data = await getClinicalHistory(fileId)
        setClinicalHistory(data?.clinical_history || '')
      } catch (error) {
        console.error('Error fetching clinical history:', error)
      }
    }

    fetchDiseases()
    fetchClinicalHistory()
  }, [fileId])

  const filesApi = useFiles()
  const fileDetailsApi = useFileDetails(fileId)
  const { data = [] } = filesApi.query
  const fileDetails = fileDetailsApi.query.data

  const [gender, setGender] = useState(null)
  const [startAge, setStartAge] = useState(null)
  const [age, setAge] = useState(null)
  const [is_inbred, setIsInbred] = useState(null)
  const [is_progressing, setProgressing] = useState(null)
  const [notes, setNotes] = useState(null)

  useEffect(() => {
    if (fileDetails) {
      setGender(fileDetails.details?.sex)
      setStartAge(fileDetails.details?.symptoms_start_age)
      setAge(fileDetails.details?.age)
      setIsInbred(fileDetails.details?.is_inbred)
      setProgressing(fileDetails.details?.is_progressing)
      setNotes(fileDetails.details?.notes)
      // Clinical history is now fetched separately
    }
  }, [fileDetails])

  const handleGenderChange = (e) => setGender(e.target.value)
  const handleStartAgeChange = (e) => setStartAge(e.target.value)
  const handleAgeChange = (e) => setAge(e.target.value)
  const handleSetNotes = (e) => setNotes(e.target.value)

  const handleDiseasesChange = (diseases) => {
    setLocalDiseases(diseases)
  }

  // Resolves true when every request succeeded, false when one failed (the
  // error is logged, as before).
  const handleSave = async () => {
    setHpoList(localHpoList)
    const details = {
      gender,
      start_age: Number(startAge),
      age: Number(age),
      is_inbred,
      is_progressing,
      notes,
    }
    const { id, type } = fileDetails.ont_bam_id
      ? { id: fileDetails.ont_bam_id, type: 'ONT_BAM' }
      : fileDetails.pacbio_bam_id
      ? { id: fileDetails.pacbio_bam_id, type: 'PACBIO_BAM' }
      : fileDetails.fastq_pair_id
      ? { id: fileDetails.fastq_pair_id, type: 'FASTQ' }
      : { id: fileDetails.vcf_id, type: 'VCF' }

    try {
      await updateDetails(id, details, type)

      if (localDiseases && localDiseases.length > 0) {
        const diseaseKeys = localDiseases.map((d) => d.key)
        await diseaseService.updateDiseaseList(fileId, diseaseKeys)
      }

      await filesApi.refresh()
      return true
    } catch (error) {
      console.error('Error saving details:', error)
      return false
    }
  }

  // The old tab only offered "Save details" once the file had a details record.
  const canSave = Boolean(fileDetails?.details)
  useEffect(() => {
    if (onCanSaveChange) onCanSaveChange(canSave)
  }, [canSave, onCanSaveChange])

  useImperativeHandle(ref, () => ({ save: handleSave }))

  const trioOptions = createTrioOptions(data)

  if (!fileDetails) {
    return (
      <Stack direction="row" justifyContent="center" sx={{ py: 8 }}>
        {fileDetailsApi.query.isError ? (
          <Typography variant="body2" color="error">
            Sample details could not be loaded.
          </Typography>
        ) : (
          <CircularProgress />
        )}
      </Stack>
    )
  }

  return (
    <Stack>
      <Grid item sx={{ minWidth: '65%', flexGrow: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            pt: 2,
            pb: 0.5,
          }}
        >
          <CardHeader title="Symptoms (HPO)" titleTypographyProps={{ variant: 'subtitle1' }} sx={{ p: 0 }} />
          {localHpoList && localHpoList.length > 0 && (
            <Chip
              label={`${localHpoList.length} ${localHpoList.length === 1 ? 'term' : 'terms'}`}
              size="small"
              sx={countChipSx}
            />
          )}
        </Box>
        <CardContent>
          <ManageHpo
            hpoList={localHpoList}
            setHpoList={setLocalHpoList}
            deleteFromDB={setHpoList}
            association={sampleName}
          />
        </CardContent>
      </Grid>
      <Grid item sx={{ minWidth: '65%', flexGrow: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            pt: 2,
            pb: 0.5,
          }}
        >
          <CardHeader title="Diseases" titleTypographyProps={{ variant: 'subtitle1' }} sx={{ p: 0 }} />
        </Box>
        <CardContent>
          {/* The section header names it; the filter's own "Selected diseases (N)"
              line carries the count, so no second label or count chip here. */}
          <DiseaseFilterWrapper
            value={localDiseases}
            onChange={handleDiseasesChange}
            arrayOutput={true}
            label={null}
            showInheritance={false}
            fileId={fileId}
          />
        </CardContent>
      </Grid>
      <Grid item container direction="row" xs={12}>
        <Grid item xs={6}>
          <CardHeader title="Clinical History & Context" titleTypographyProps={{ variant: 'subtitle1' }} />
        </Grid>
        <Grid item xs={6}>
          <CardHeader title="Notes" titleTypographyProps={{ variant: 'subtitle1' }} sx={{ paddingLeft: 0 }} />
        </Grid>
      </Grid>
      <Grid item container direction="row" xs={12}>
        <Grid item xs={6}>
          <CardContent>
            <TextField
              fullWidth
              id="details-clinical-history"
              value={clinicalHistory}
              InputProps={{
                readOnly: true,
              }}
              multiline
              rows={5}
              placeholder="No clinical history available"
            />
          </CardContent>
        </Grid>
        <Grid item xs={6}>
          <CardContent sx={{ paddingLeft: 0 }}>
            <TextField
              fullWidth
              id="details-notes"
              value={notes ?? ''}
              onChange={handleSetNotes}
              multiline
              rows={5}
              placeholder="Enter additional notes..."
            />
          </CardContent>
        </Grid>
      </Grid>
      {fileDetails?.details && (
        <Grid item container direction="row" xs={12}>
          <Grid container item xs={6} sx={{ flexGrow: 1 }}>
            <Grid item xs={12}>
              <div style={{ display: 'none' }}>
                <CardContent>
                  {fileDetails && (
                    <TrioSelector
                      trio={fileDetails?.trio ? fileDetails.trio : null}
                      options={trioOptions}
                      samples={sampleName}
                      fileId={fileId}
                    />
                  )}
                </CardContent>
              </div>
            </Grid>
            <Grid item xs={12} sx={{ flexGrow: 1 }}>
              <CardHeader title="Details" titleTypographyProps={{ variant: 'subtitle1' }} sx={{ paddingTop: 0 }} />
            </Grid>
            <Grid container direction={'row'} item xs={12} height={'auto'}>
              <Grid container item xs={6} direction={'row'}>
                <Grid item xs={12}>
                  <CardContent>
                    <FormControl fullWidth>
                      <InputLabel id="select-age">Age</InputLabel>
                      <Input type="number" value={age ?? ''} onChange={handleAgeChange}></Input>
                    </FormControl>
                  </CardContent>
                </Grid>
                <Grid item xs={12}>
                  <CardContent>
                    <FormControl fullWidth>
                      <InputLabel id="select-start-age">Symptoms start age</InputLabel>
                      <Input type="number" value={startAge ?? ''} onChange={handleStartAgeChange}></Input>
                    </FormControl>
                  </CardContent>
                </Grid>
              </Grid>
              <Grid container item xs={6} direction={'row'}>
                <Grid xs={12} item>
                  <CardContent>
                    <FormControl fullWidth>
                      <InputLabel id="select-gender">Gender</InputLabel>
                      <Select labelId="select-gender" value={gender || ''} onChange={handleGenderChange} label="Gender">
                        <MenuItem value={'XX'}>XX</MenuItem>
                        <MenuItem value={'XY'}>XY</MenuItem>
                        <MenuItem value={'XXX'}>XXX</MenuItem>
                        <MenuItem value={'XYY'}>XYY</MenuItem>
                        <MenuItem value={'XXY'}>XXY</MenuItem>
                        <MenuItem value={'XXXX'}>XXXX</MenuItem>
                        <MenuItem value={'XXXY'}>XXXY</MenuItem>
                        <MenuItem value={'XXYY'}>XXYY</MenuItem>
                        <MenuItem value={'XXXXX'}>XXXXX</MenuItem>
                        <MenuItem value={'XXXXY'}>XXXXY</MenuItem>
                      </Select>
                    </FormControl>
                  </CardContent>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Stack>
  )
})

export default SampleDetailsPanel
